import { Table, Switch, Tag, Popconfirm } from 'antd';
import { Eye, Edit, Trash2 } from 'lucide-react';
import type { FC } from 'react';
import type { TablePaginationConfig } from 'antd';

export interface KnowledgeBrick {
  id: string;
  category: string;
  content: string;
  sourceType: string;
  personaTarget: string | string[];
  isVerified: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  data: KnowledgeBrick[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (page: number, pageSize: number) => void;
  onView: (brick: KnowledgeBrick) => void;
  onEdit: (brick: KnowledgeBrick) => void;
  onDelete: (id: string) => void;
  onToggleVerified: (id: string, verified: boolean) => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  argument_vente: 'blue',
  case_study: 'green',
  technical_spec: 'purple',
  testimonial: 'gold',
  competitive_advantage: 'red',
  faq: 'default',
  objection_response: 'geekblue',
};

const CATEGORY_LABELS: Record<string, string> = {
  argument_vente: 'Argument vente',
  case_study: 'Case study',
  technical_spec: 'Spec technique',
  testimonial: 'Témoignage',
  competitive_advantage: 'Avantage conc.',
  faq: 'FAQ',
  objection_response: 'Rép. objection',
};

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  markdown: { label: 'MD', color: 'cyan' },
  feedback: { label: 'FB', color: 'orange' },
  manual: { label: 'Manual', color: 'default' },
};

const truncate = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max)}…` : text;

const KnowledgeBrickTable: FC<Props> = ({
  data,
  total,
  page,
  pageSize,
  loading,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onToggleVerified,
}) => {
  const pagination: TablePaginationConfig = {
    current: page,
    pageSize,
    total,
    onChange: onPageChange,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50'],
    showTotal: (t) => `${t} brique${t > 1 ? 's' : ''}`,
    size: 'small',
  };

  return (
    <div className="fc-card overflow-hidden">
      <Table<KnowledgeBrick>
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        size="middle"
        scroll={{ x: 800 }}
        onRow={(record) => ({
          onClick: () => onView(record),
          className: 'cursor-pointer',
        })}
      >
        <Table.Column<KnowledgeBrick>
          title="Catégorie"
          dataIndex="category"
          width={150}
          render={(cat: string) => (
            <Tag color={CATEGORY_COLORS[cat] ?? 'default'}>
              {CATEGORY_LABELS[cat] ?? cat}
            </Tag>
          )}
        />

        <Table.Column<KnowledgeBrick>
          title="Contenu"
          dataIndex="content"
          ellipsis
          render={(text: string) => (
            <span className="text-sm text-slate-700 dark:text-slate-300">
              {truncate(text, 80)}
            </span>
          )}
        />

        <Table.Column<KnowledgeBrick>
          title="Source"
          dataIndex="sourceType"
          width={90}
          render={(src: string) => {
            const s = SOURCE_LABELS[src];
            return <Tag color={s?.color ?? 'default'}>{s?.label ?? src}</Tag>;
          }}
        />

        <Table.Column<KnowledgeBrick>
          title="Persona"
          dataIndex="personaTarget"
          width={120}
          render={(p: string | string[]) => {
            const list = Array.isArray(p) ? p : p ? [p] : [];
            return (
              <div className="flex flex-wrap gap-1">
                {list.map((persona) => (
                  <Tag key={persona} className="uppercase text-[10px]">
                    {persona}
                  </Tag>
                ))}
              </div>
            );
          }}
        />

        <Table.Column<KnowledgeBrick>
          title="Vérifié"
          dataIndex="isVerified"
          width={80}
          align="center"
          render={(verified: boolean, record) => (
            <Switch
              size="small"
              checked={verified}
              onChange={(checked, e) => {
                e.stopPropagation();
                onToggleVerified(record.id, checked);
              }}
            />
          )}
        />

        <Table.Column<KnowledgeBrick>
          title="Usages"
          dataIndex="usageCount"
          width={75}
          align="center"
          render={(count: number) => (
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {count}
            </span>
          )}
        />

        <Table.Column<KnowledgeBrick>
          title="Actions"
          width={120}
          align="center"
          render={(_, record) => (
            <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => onView(record)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400 transition-colors"
                title="Voir"
              >
                <Eye className="w-4 h-4" />
              </button>
              <button
                onClick={() => onEdit(record)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-amber-50 hover:text-amber-600 dark:hover:bg-amber-900/30 dark:hover:text-amber-400 transition-colors"
                title="Modifier"
              >
                <Edit className="w-4 h-4" />
              </button>
              <Popconfirm
                title="Supprimer cette brique ?"
                description="Cette action est irréversible."
                onConfirm={() => onDelete(record.id)}
                okText="Supprimer"
                cancelText="Annuler"
                okButtonProps={{ danger: true }}
              >
                <button
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </Popconfirm>
            </div>
          )}
        />
      </Table>
    </div>
  );
};

export default KnowledgeBrickTable;
